import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { decodeJwt } from "jose";
import {
  changePassword,
  isInvalidOldPasswordError,
  isLoginRequest,
  isRefreshTokenRequest,
  isUnauthorizedResponse,
  login,
  logout,
  refreshAllTokens,
} from "../services/auth.js";
import {
  showErrorNotification,
  showSuccessNotification,
} from "./notifications.js";

const initialState = {
  changePassword: {
    isInProgress: false,
  },
  errorMessage: null,
  user: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.fulfilled, (state) => ({
        ...state,
        errorMessage: null,
      }))
      .addCase(loginUser.rejected, (state, { error }) => ({
        ...state,
        errorMessage: error.message,
        user: null,
      }))
      .addCase(setAccessToken.fulfilled, (state, { payload }) => ({
        ...state,
        user: payload,
      }))
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.changePassword.isInProgress = false;
      })
      .addCase(changeUserPassword.pending, (state) => {
        state.changePassword.isInProgress = true;
      })
      .addCase(changeUserPassword.rejected, (state) => {
        state.changePassword.isInProgress = false;
      });
  },
});

export default authSlice.reducer;

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  ({ email, password }, { dispatch }) =>
    login({ email, password })
      .then(({ accessToken }) => {
        dispatch(setAccessToken(accessToken));
      })
      .catch((err) => {
        dispatch(showErrorNotification(err.message));
        throw err;
      }),
);

const setAccessToken = createAsyncThunk(
  "auth/setAccessToken",
  (accessToken) => {
    setAuthorizationHeaderToApis(accessToken);
    return accessToken ? decodeJwt(accessToken) : null;
  },
);

export const selectAuthErrorMessage = (state) => state.auth.errorMessage;

export const selectLoggedInUser = (state) => state.auth.user;

const selectHasScopes = createSelector(
  (_, scopes) => scopes,
  selectLoggedInUser,
  (scopes, user) =>
    (user?.scope?.split(" ") || []).some((s) => (scopes || []).includes(s)),
);

export const selectCanEditAllSales = (state) =>
  selectHasScopes(state, ["sales:*:a", "sales:w:a"]);

export const selectCanEditCustomers = (state) =>
  selectHasScopes(state, ["cus:*", "cus:w"]);

export const selectCanEditCustomersWithGds = (state) =>
  selectHasScopes(state, ["suba:*", "suba:w"]);

export const selectCanEditSuppliers = (state) =>
  selectHasScopes(state, ["sup:*", "sup:w"]);

export const selectIsChangePasswordInProgress = (state) =>
  state.auth.changePassword.isInProgress;

export const selectCanEditChartOfAccounts = (state) =>
  selectHasScopes(state, ["coa:*", "coa:w"]);

export const ERR_MSG_INVALID_OLD_PASSWORD = "Invalid old password";

export const changeUserPassword = createAsyncThunk(
  "auth/changeUserPassword",
  ({ newPassword, oldPassword }, { dispatch }) => {
    return changePassword({ newPassword, oldPassword })
      .then(({ accessToken }) => {
        dispatch(setAccessToken(accessToken));
        dispatch(showSuccessNotification("User password has been changed"));
      })
      .catch((err) => {
        if (isInvalidOldPasswordError(err)) {
          dispatch(showErrorNotification(ERR_MSG_INVALID_OLD_PASSWORD));
          throw new Error(ERR_MSG_INVALID_OLD_PASSWORD);
        } else {
          dispatch(showErrorNotification(err.message));
        }
        throw err;
      });
  },
);

export const logoutUser = createAsyncThunk("auth/logout", (_, { dispatch }) => {
  return logout().finally(() => {
    dispatch(setAccessToken(""));
  });
});

const registeredApis = [];

const getHeadersForAccessToken = (token) => ({
  Authorization: token ? `Bearer ${token}` : "",
});

export const refreshAccessToken = createAsyncThunk(
  "auth/refreshAccessToken",
  (_, { dispatch }) => {
    return refreshAllTokens()
      .catch((err) => {
        dispatch(logoutUser());
        const errMessage = "Your session has expired. Please login again.";
        dispatch(showErrorNotification(errMessage));
        throw new Error(errMessage);
      })
      .then(({ accessToken }) => {
        dispatch(setAccessToken(accessToken));
        return accessToken;
      });
  },
);

/* refreshTokensHandler :: Store -> Request -> Response -> Promise Response */
const refreshTokensHandler = (store) => (request) => (response) => {
  if (
    isUnauthorizedResponse(response) &&
    !isRefreshTokenRequest(request) &&
    !isLoginRequest(request)
  ) {
    return store
      .dispatch(refreshAccessToken())
      .unwrap()
      .then((accessToken) => {
        const { Authorization } = getHeadersForAccessToken(accessToken);
        request.headers.set("Authorization", Authorization);
        return fetch(request);
      });
  }
  return Promise.resolve(response);
};

export const registerApis = (store) => (apis) => {
  apis.forEach((api) => {
    registeredApis.push(api);
    api.useResponse(refreshTokensHandler(store));
  });
};

const setAuthorizationHeaderToApis = (accessToken) => {
  registeredApis.forEach((api) =>
    api.setHeaders(getHeadersForAccessToken(accessToken)),
  );
};
